import React, { useCallback, useContext } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import styled from 'styled-components'
import { rem } from 'polished'
import HeadingTextBlock from 'LuxKit/TextBlocks/HeadingTextBlock'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import {
  BRAND_SIGNATURE_SERIES,
  TOUR_ASIA_PROMO_REGIONS,
  TOUR_ASIA_TAKEOVER_SEARCH_PLACE_IDS,
  TOUR_V2_HERO_IMAGE_ASIA_MOBILE_BACKGROUND,
} from 'constants/tours'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Pill from 'LuxKit/Pill'
import TextLink from 'LuxKit/TextLink'
import Group from 'components/utils/Group'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import { useAppDispatch } from 'hooks/reduxHooks'
import { pushWithRegion } from 'actions/NavigationActions'
import { scrollToId } from 'lib/window/scrollUtils'
import Places from 'constants/places'
import { LUXURY_ESCAPES } from 'constants/brands'
import { mediaQueryUp } from 'components/utils/breakpoint'
import HeroImage from 'content/components/HeroImage'
import FormatCurrency from 'components/Common/FormatCurrency/FormatCurrency'
import GeoContext from 'contexts/geoContext'

const BackgroundImage = styled(HeroImage)`
  min-height: ${rem(165)};

  ${mediaQueryUp.tablet} {
    min-height: ${rem(200)};
  }
`

const TourAsiaImageHeader = styled(Group)`
  position: relative;
  z-index: 1;
`

interface Props {
  modalVariant?: 'offer' | 'generic'
  onOfferClick?: () => void
}

function TourAsiaCampaignModal(props: Props) {
  const { currentRegionCode, currentCurrency } = useContext(GeoContext)
  const modalVariant = props.modalVariant ?? 'generic'
  const modalContext = useModalElementContext()
  const appDispatch = useAppDispatch()
  const intl = useIntl()
  const handleClick = useCallback(() => {
    if (modalVariant === 'generic') {
      const urlSearchParams = new URLSearchParams({
        destinationId: Places.Asia.id,
        destinationName: Places.Asia.name,
        destinations: TOUR_ASIA_TAKEOVER_SEARCH_PLACE_IDS.toString(),
        operators: [BRAND_SIGNATURE_SERIES, LUXURY_ESCAPES].toString(),
      })
      appDispatch(pushWithRegion(`/search/tours?${urlSearchParams.toString()}`))
    } else if (modalVariant === 'offer') {
      scrollToId('TOUR_DEPARTURE_DATES')
    }

    modalContext.resolve()
  }, [modalVariant, modalContext, appDispatch])
  const viewAvailabilitiesLabel = intl.formatMessage({
    id: 'cDYsR8cm5w2fAtCG7Olg',
    defaultMessage: 'View availabilities',
    description: 'Button text to view tour departure dates in Tour Asia campaign modal',
  })
  const viewAllOffersLabel = intl.formatMessage({
    id: 'KxSt2Yo4mcgNpASTUUAW',
    defaultMessage: 'View all offers',
    description: 'Button text to view all tour offers in Tour Asia campaign modal',
  })
  return <ModalBase>
    <ModalHeader title="" isOverlay>
    </ModalHeader>
    <ModalBody>
      <BackgroundImage
        id={TOUR_V2_HERO_IMAGE_ASIA_MOBILE_BACKGROUND}
        fit="center"
        gravity="auto"
      >
        <TourAsiaImageHeader fullWidth direction="vertical" horizontalAlign="start" verticalAlign="center" gap={4}>
          <HeadingTextBlock colour="neutral-eight" variant="heading1" align="start">
            {intl.formatMessage({
              id: '6AR87IjbTTgt4FuVyR5O',
              defaultMessage: 'The best of',
              description: 'Tour Asia campaign modal hero image first line heading',
            })}
          </HeadingTextBlock>
          <HeadingTextBlock colour="neutral-eight" variant="heading1" align="start">
            <FormattedMessage
              id="Ex7SbS4U0o6dsrISV3Eh"
              defaultMessage="<italic>Asia</italic> on Tour"
              description="Tour Asia campaign modal hero image second line heading with italic Asia"
              values={{
                italic: (chunks) => <i>{chunks}</i>,
              }}
            />
          </HeadingTextBlock>
          <HeadingTextBlock colour="neutral-eight" variant="heading6" align="start">
            <FormattedMessage
              id="1XwxZ15VtG1R4rBtyGxO"
              defaultMessage="Use code <pill>TOURASIA</pill> at checkout"
              description="Tour Asia campaign modal promo code instruction heading"
              values={{
                pill: (chunks) => <Pill kind="primary" variant="success">{chunks}</Pill>,
              }}
            />
          </HeadingTextBlock>
        </TourAsiaImageHeader>
      </BackgroundImage>
      <ModalContent>
        <VerticalSpacer gap={12}>
          <HeadingTextBlock variant="heading4">
            <FormattedMessage
              id="Bakft7hSRqj6FwoHmBUY"
              defaultMessage="Save up to {amount} off tours across Asia"
              description="Tour Asia campaign modal heading showing maximum discount amount"
              values={{
                amount: <FormatCurrency format="dynamic" currency={currentCurrency} value={TOUR_ASIA_PROMO_REGIONS[currentRegionCode]} />,
              }}
            />
          </HeadingTextBlock>
          <BodyTextBlock variant="medium">
            <FormattedMessage
              id="_uGqLlbsSgSzo8ek1Wzt"
              defaultMessage="Save 5% off tours across Asia. Minimum spend applies. Offer applies to {brandName} Tours only. Code can only be used once per customer. Offer ends at 11:59pm (AEST) on 28th April 2025. <link>Terms & Conditions apply.</link>"
              description="Tour Asia campaign modal promotional terms body text with link to terms and conditions"
              values={{
                brandName: 'Luxury Escapes',
                link: (chunks) => <TextLink weight="regular" to="/promotions">{chunks}</TextLink>,
              }}
            />
          </BodyTextBlock>
        </VerticalSpacer>
      </ModalContent>
    </ModalBody>
    <ModalFooter
      primaryActionProps={{
        children: (modalVariant === 'offer') ? viewAvailabilitiesLabel : viewAllOffersLabel,
        onClick: handleClick,
      }}
      actionsLayout="auto"
    >
    </ModalFooter>
  </ModalBase>
}

export default TourAsiaCampaignModal
